import { AxiosInstance } from 'axios'
import { useMutation, useQueryClient } from 'react-query'

import {
  BasicTeam,
  TEAMS_BASE_QUERY_KEY,
  TeamDetailsData,
} from '@dc-shared-web/hooks/api/team'

import { useAxiosContext } from '@dc-shared-web/providers/AxiosProvider'

import { useToast } from '@dc-shared-web/components/shadcn/use-toast'
import { User } from '@dc-shared-web/index'

interface TeamUpdate {
  name?: string
  customerId?: string | null
  active?: boolean
  internal?: boolean
  trial?: boolean
  partner?: boolean
  entitlements?: { [key: string]: boolean }
  solutionTypes?: {
    originUUID: string
    name: string
  }[]
}

interface UpdateTeamParams {
  invalidateUser?: boolean
  team: TeamUpdate & { teamUuid: string }
}

const updateTeam = async (
  apiClient: AxiosInstance,
  params: UpdateTeamParams
): Promise<TeamDetailsData> => {
  const updateVals = {
    name: params.team.name,
    customerId: params.team.customerId,
    active: params.team.active,
    internal: params.team.internal,
    trial: params.team.trial,
    partner: params.team.partner,
    entitlements: params.team.entitlements,
    solutionTypes: params.team.solutionTypes,
  } as TeamUpdate

  const { data } = await apiClient.patch(
    `/api/teams/${params.team.teamUuid}`,
    updateVals
  )

  return data.data
}

export const useUpdateTeam = (params?: {
  onSuccess?: (data: TeamDetailsData) => void
  onError?: () => void
}) => {
  const apiClient = useAxiosContext()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation(
    (mutationParams: UpdateTeamParams) => updateTeam(apiClient, mutationParams),
    {
      onSuccess: (data, mutationParams) => {
        queryClient.invalidateQueries<BasicTeam[]>(
          `${TEAMS_BASE_QUERY_KEY}Details`,
          { refetchActive: true, refetchInactive: true }
        )
        queryClient.invalidateQueries<TeamDetailsData>(
          `team-${mutationParams.team.teamUuid}`,
          { refetchActive: true, refetchInactive: true }
        )

        // Only invalidate user if the user is associated to the team that's being updated
        if (mutationParams.invalidateUser) {
          queryClient.invalidateQueries<User>(`User`)
        }
        params?.onSuccess?.(data)
      },
      onError: () => {
        toast({
          variant: 'error',
          title: 'Error updating team',
          description: 'If the issue persists, please contact support.',
        })
        params?.onError?.()
      },
    }
  )
}
